import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface SiteStats {
  site: { status: 'online' | 'offline'; responseTimeMs: number; url: string };
  active: number;
  today: { unique: number; total: number };
  month: { unique: number; total: number };
  daily: Array<{ date: string; count: number }>;
  topPages: Array<{ path: string; views: number }>;
  topReferrers: Array<{ referrer: string; views: number }>;
}

function StatusDot({ status }: { status: 'online' | 'offline' }) {
  return (
    <span
      className={`inline-block w-3 h-3 rounded-full ${
        status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-red-500'
      }`}
    />
  );
}

function StatCard({
  label,
  value,
  sub,
  color = 'rla',
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">{label}</div>
      <div className={`text-3xl font-bold text-${color}-600`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function MiniBar({ data, max }: { data: Array<{ date: string; count: number }>; max: number }) {
  const barMax = max || 1;
  return (
    <div className="flex items-end gap-2 h-32">
      {data.map((d) => {
        const pct = Math.max(4, (d.count / barMax) * 100);
        const day = new Date(d.date).toLocaleDateString('de-DE', { weekday: 'short' });
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-[10px] text-slate-500 font-medium">{d.count}</span>
            <div
              className="w-full bg-rla-500 rounded-t-md transition-all duration-500"
              style={{ height: `${pct}%`, minHeight: '4px' }}
            />
            <span className="text-[10px] text-slate-400">{day}</span>
          </div>
        );
      })}
    </div>
  );
}

export function WebsiteAnalyticsPage() {
  const [stats, setStats] = useState<SiteStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get<SiteStats>('/website/stats');
      setStats(data);
    } catch {
      setError('Statistiken konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), 30_000); // Alle 30s aktualisieren
    return () => clearInterval(interval);
  }, []);

  if (loading && !stats) {
    return <div className="text-slate-500">Lade Website-Statistiken …</div>;
  }

  if (error && !stats) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
        {error}
        <button onClick={() => void load()} className="ml-3 underline">
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (!stats) return null;

  const dailyMax = Math.max(...stats.daily.map((d) => d.count), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Website Analytics</h1>
          <p className="text-sm text-slate-500">rla-solutions.de — Live-Übersicht</p>
        </div>
        <button
          onClick={() => void load()}
          className="px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
        >
          Aktualisieren
        </button>
      </div>

      {/* Status-Banner */}
      <div
        className={`flex items-center gap-4 px-5 py-4 rounded-xl border ${
          stats.site.status === 'online'
            ? 'bg-green-50 border-green-200'
            : 'bg-red-50 border-red-200'
        }`}
      >
        <StatusDot status={stats.site.status} />
        <div className="flex-1">
          <div className="font-semibold text-slate-800">
            {stats.site.status === 'online' ? 'Website ist online' : 'Website ist offline'}
          </div>
          <div className="text-xs text-slate-500">
            {stats.site.url}
            {stats.site.responseTimeMs > 0 && ` — ${stats.site.responseTimeMs}ms Antwortzeit`}
          </div>
        </div>
        <a
          href={stats.site.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-rla-600 hover:underline"
        >
          Öffnen ↗
        </a>
      </div>

      {/* Stat-Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Gerade online"
          value={stats.active}
          sub="Besucher in den letzten 5 Min."
          color="green"
        />
        <StatCard
          label="Heute"
          value={stats.today.unique}
          sub={`${stats.today.total} Seitenaufrufe`}
        />
        <StatCard
          label="Diesen Monat"
          value={stats.month.unique}
          sub={`${stats.month.total} Seitenaufrufe gesamt`}
        />
        <StatCard
          label="Antwortzeit"
          value={stats.site.responseTimeMs > 0 ? `${stats.site.responseTimeMs}ms` : '—'}
          sub="Letzte Messung"
        />
      </div>

      {/* Chart + Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 7-Tage-Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">Besucher — Letzte 7 Tage</h2>
          <MiniBar data={stats.daily} max={dailyMax} />
        </div>

        {/* Top-Seiten */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Top-Seiten (Monat)</h2>
          {stats.topPages.length === 0 ? (
            <div className="text-sm text-slate-400">Noch keine Daten</div>
          ) : (
            <ul className="space-y-2">
              {stats.topPages.map((p) => (
                <li key={p.path} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 truncate">{p.path}</span>
                  <span className="text-slate-400 font-mono text-xs">{p.views}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Referrers */}
      {stats.topReferrers.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Top-Referrer (Monat)</h2>
          <ul className="space-y-2">
            {stats.topReferrers.map((r) => (
              <li key={r.referrer} className="flex items-center justify-between text-sm">
                <span className="text-slate-600 truncate">{r.referrer}</span>
                <span className="text-slate-400 font-mono text-xs">{r.views}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Auto-refresh note */}
      <div className="text-xs text-slate-400 text-center">
        Daten aktualisieren sich automatisch alle 30 Sekunden
      </div>
    </div>
  );
}
